// Vorbelegungs-Quellen für Signatur-Felder: ein Feld kann an eine Datenquelle
// gebunden werden (feld.quelle); wählt der Absender in der Pille den Bezug
// (Objekt/Einheit/Mieter/Eigentümer/Kontakt), werden die Werte automatisch
// eingetragen und erscheinen auf der Signier-Seite als Startwerte (vom
// Unterzeichner änderbar). Der Katalog und die reinen Abbildungen liegen in
// lib/signatur/quellen.ts, hier steht nur die Datenbeschaffung.
import { supabase } from '../../../lib/supabase/client';
import {
  QUELLEN, datumHeute, eigentuemerWerte, kontaktWerte, mieterWerte, objektWerte,
} from '../../../lib/signatur/quellen';

export { QUELLEN, QUELLEN_SCHLUESSEL, kontaktAnzeigename } from '../../../lib/signatur/quellen';

export interface Bezug {
  objekt_id?: string | null;
  einheit_id?: string | null;
  mieter_id?: string | null;
  eigentuemer_id?: string | null;
  kontakt_id?: string | null;
  // Anzeige-Namen (damit die Pille nach dem Neuöffnen nicht nachladen muss)
  objekt_name?: string | null;
  einheit_name?: string | null;
  mieter_name?: string | null;
  eigentuemer_name?: string | null;
  kontakt_name?: string | null;
}

/** Zentrale Gläubiger-Identifikationsnummer der Verwaltung (app_settings). */
export async function ladeZentraleGlaeubigerId(): Promise<string> {
  const { data } = await supabase.from('app_settings').select('value').eq('key', 'sepa_glaeubiger_id').maybeSingle();
  const v: any = data?.value;
  return typeof v === 'string' ? v.trim() : '';
}

export async function speichereZentraleGlaeubigerId(wert: string): Promise<void> {
  const { error } = await supabase
    .from('app_settings')
    .upsert({ key: 'sepa_glaeubiger_id', value: wert.trim(), updated_at: new Date().toISOString() }, { onConflict: 'key' });
  if (error) throw error;
}

/** Lädt die Bezugsdaten und liefert die Werte je Quelle (nur vorhandene Quellen). */
export async function ladeQuellenWerte(bezug: Bezug): Promise<Record<string, string>> {
  const werte: Record<string, string> = { 'datum.heute': datumHeute(new Date()) };

  const [mieterQ, eigQ, kontaktQ, objQ, einQ, zentraleId] = await Promise.all([
    bezug.mieter_id
      ? supabase.from('mieter').select('vorname, nachname, email, telefon, einheit, mieter_iban, sepa_mandatsreferenz, kundennummer, objekt:objekt_id(strasse, hausnummer, plz, stadt)').eq('id', bezug.mieter_id).maybeSingle()
      : Promise.resolve({ data: null }),
    bezug.eigentuemer_id
      ? supabase.from('eigentuemer').select('vorname, nachname, firma_name, email, strasse, plz, stadt, glaeubiger_id, sepa_mandatsreferenz, kundennummer').eq('id', bezug.eigentuemer_id).maybeSingle()
      : Promise.resolve({ data: null }),
    bezug.kontakt_id
      ? supabase.from('kontakte').select('vorname, nachname, firma_name, email, strasse, hausnummer, plz, stadt, glaeubiger_id, iban, bic, kontoinhaber').eq('id', bezug.kontakt_id).maybeSingle()
      : Promise.resolve({ data: null }),
    bezug.objekt_id
      ? supabase.from('objekte').select('bezeichnung, strasse, hausnummer, plz, stadt').eq('id', bezug.objekt_id).maybeSingle()
      : Promise.resolve({ data: null }),
    bezug.einheit_id
      ? supabase.from('objekt_einheiten').select('bezeichnung').eq('id', bezug.einheit_id).maybeSingle()
      : Promise.resolve({ data: null }),
    ladeZentraleGlaeubigerId(),
  ]);

  if (zentraleId) werte['verwaltung.glaeubiger_id'] = zentraleId;
  Object.assign(
    werte,
    mieterWerte(mieterQ.data),
    eigentuemerWerte(eigQ.data, zentraleId),
    kontaktWerte(kontaktQ.data),
    objektWerte(objQ.data),
  );

  const ein: any = einQ.data;
  if (ein?.bezeichnung) werte['einheit.bezeichnung'] = ein.bezeichnung;

  return werte;
}
